import React, { useEffect, useRef, useState } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  getFirestore,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import app from "../../firebase.js";

const profiles = [
  {
    id: "particulier",
    icon: "👤",
    title: "Particulier",
    description:
      "Financez un besoin personnel, familial, médical ou l’achat d’un équipement.",
    examples: "Prêt personnel, santé, logement, mariage",
    color: "#7c3aed",
    background: "#f5f3ff",
  },
  {
    id: "entreprise",
    icon: "🏢",
    title: "Entreprise",
    description:
      "Renforcez votre trésorerie et financez les besoins de développement de votre activité.",
    examples: "Salaires, stock, matériel, fournisseurs",
    color: "#2563eb",
    background: "#eff6ff",
  },
  {
    id: "etudiant",
    icon: "🎓",
    title: "Étudiant",
    description:
      "Obtenez un accompagnement pour poursuivre vos études et financer votre formation.",
    examples: "Scolarité, ordinateur, logement, formation",
    color: "#0891b2",
    background: "#ecfeff",
  },
  {
    id: "diaspora",
    icon: "🌍",
    title: "Diaspora",
    description:
      "Financez un projet ou accompagnez votre famille et vos investissements en Guinée.",
    examples: "Immobilier, commerce, agriculture, famille",
    color: "#059669",
    background: "#ecfdf5",
  },
];

function FinanceHome() {
  const navigate = useNavigate();
  const profilesSectionRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    const auth = getAuth(app);
    const database = getFirestore(app);

    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        setCurrentUser(currentUser);

        if (!currentUser) {
          setIsAdmin(false);
          return;
        }

        try {
          const adminDocument = await getDoc(
            doc(database, "admins", currentUser.uid)
          );

          setIsAdmin(adminDocument.exists());
        } catch (error) {
          console.error(
            "Erreur lors de la vérification administrateur :",
            error
          );

          setIsAdmin(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    const confirmation = window.confirm(
      "Voulez-vous vraiment vous déconnecter de votre compte Groupe HDC ?"
    );

    if (!confirmation) {
      return;
    }

    setLogoutLoading(true);

    try {
      await signOut(getAuth(app));
      setCurrentUser(null);
      setIsAdmin(false);
    } catch (error) {
      console.error("Erreur lors de la déconnexion :", error);
      window.alert(
        "La déconnexion a échoué. Veuillez réessayer."
      );
    } finally {
      setLogoutLoading(false);
    }
  };

  const scrollToProfiles = () => {
    profilesSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleProfileSelection = (profileId) => {
    navigate(`/consulting/finance/demande?profil=${profileId}`);
  };

  return (
    <div style={styles.page}>

    <div style={styles.topBar}>
  <button
    type="button"
    style={styles.backButton}
    onClick={() => navigate("/consulting")}
  >
    ← Retour à l’accueil HDC Consulting
  </button>
</div>

      {currentUser && (
        <div style={styles.accountBar}>
          <div style={styles.accountIdentity}>
            <div style={styles.accountIcon}>
              {currentUser.email?.charAt(0).toUpperCase() || "U"}
            </div>

            <div style={styles.accountInformation}>
              <span style={styles.accountLabel}>
                Compte connecté
              </span>
              <strong style={styles.accountEmail}>
                {currentUser.email}
              </strong>
            </div>
          </div>

          <button
            type="button"
            style={{
              ...styles.logoutButton,
              ...(logoutLoading ? styles.disabledButton : {}),
            }}
            disabled={logoutLoading}
            onClick={handleLogout}
          >
            {logoutLoading ? "Déconnexion…" : "Se déconnecter"}
          </button>
        </div>
      )}

      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <div style={styles.badge}>💳 HDC Financement</div>

          <h1 style={styles.title}>
            Une solution simple pour présenter votre demande de financement
          </h1>

          <p style={styles.subtitle}>
            Déposez votre demande, ajoutez vos justificatifs et suivez
            l’avancement de votre dossier depuis votre espace personnel.
          </p>

          <div style={styles.actions}>
            <button
              type="button"
              style={styles.primaryButton}
              onClick={scrollToProfiles}
            >
              Faire une demande
              <span style={styles.arrow}>→</span>
            </button>

            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate("/consulting/finance/mes-demandes")}
            >
              Suivre mes demandes
            </button>

            {isAdmin && (
              <button
                type="button"
                style={styles.adminButton}
                onClick={() =>
                  navigate("/consulting/finance/admin")
                }
              >
                ⚙️ Administration Finance
              </button>
            )}
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div style={styles.summaryHeader}>
            <div>
              <div style={styles.summaryLabel}>Exemple de dossier</div>
              <div style={styles.summaryTitle}>Projet personnel</div>
            </div>

            <div style={styles.summaryIcon}>📄</div>
          </div>

          <div style={styles.summaryBody}>
            <SummaryRow label="Montant demandé" value="50 000 000 GNF" />
            <SummaryRow label="Durée souhaitée" value="12 mois" />

            <SummaryRow
              label="Statut"
              value={
                <span style={styles.statusBadge}>En cours d’étude</span>
              }
            />
          </div>

          <div style={styles.progressBlock}>
            <div style={styles.progressHeader}>
              <span>Progression du dossier</span>
              <strong>75 %</strong>
            </div>

            <div style={styles.progressTrack}>
              <div style={styles.progressBar} />
            </div>
          </div>
        </div>
      </section>

      <section ref={profilesSectionRef} style={styles.profilesSection}>
        <div style={styles.sectionHeading}>
          <div style={styles.sectionEyebrow}>Votre besoin</div>

          <h2 style={styles.sectionTitle}>Choisissez votre profil</h2>

          <p style={styles.sectionText}>
            Sélectionnez la catégorie qui correspond à votre situation. Nous
            vous proposerons ensuite les solutions de financement adaptées à
            votre profil.
          </p>
        </div>

        <div style={styles.profilesGrid}>
          {profiles.map((profile) => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              onSelect={() => handleProfileSelection(profile.id)}
            />
          ))}
        </div>

        <div style={styles.profileNotice}>
          <span style={styles.noticeIcon}>🔒</span>

          <p style={styles.noticeText}>
            La sélection de votre profil nous permet uniquement d’adapter le
            parcours de demande. Vos informations resteront confidentielles et
            sécurisées.
          </p>
        </div>
      </section>

      <section style={styles.featuresSection}>
        <div style={styles.sectionHeading}>
          <div style={styles.sectionEyebrow}>Fonctionnement</div>

          <h2 style={styles.sectionTitle}>
            Une demande claire et sécurisée
          </h2>

          <p style={styles.sectionText}>
            Votre dossier reste privé et accessible uniquement par vous et
            l’administrateur chargé de son traitement.
          </p>
        </div>

        <div style={styles.featuresGrid}>
          <FeatureCard
            icon="📝"
            title="Remplissez votre demande"
            text="Indiquez le montant souhaité, la durée et la raison de votre demande."
          />

          <FeatureCard
            icon="📎"
            title="Ajoutez vos justificatifs"
            text="Téléversez vos documents PDF et images en toute simplicité."
          />

          <FeatureCard
            icon="🔍"
            title="Analyse administrative"
            text="L’administrateur étudie votre dossier et peut demander des informations complémentaires."
          />

          <FeatureCard
            icon="📩"
            title="Recevez une décision"
            text="Vous êtes informé lorsque votre demande est acceptée, refusée ou mise à jour."
          />
        </div>
      </section>

      <section style={styles.infoSection}>
        <div style={styles.infoCard}>
          <div>
            <h2 style={styles.infoTitle}>Présentez-nous votre projet</h2>

            <p style={styles.infoText}>
              L’envoi d’une demande ne constitue pas une acceptation
              automatique. Chaque dossier est étudié individuellement avant
              toute décision.
            </p>
          </div>

          <button
            type="button"
            style={styles.darkButton}
            onClick={scrollToProfiles}
          >
            Commencer ma demande →
          </button>
        </div>
      </section>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={styles.summaryRow}>
      <span style={styles.summaryRowLabel}>{label}</span>
      <span style={styles.summaryRowValue}>{value}</span>
    </div>
  );
}

function ProfileCard({ profile, onSelect }) {
  return (
    <button
      type="button"
      style={styles.profileCard}
      onClick={onSelect}
      aria-label={`Choisir le profil ${profile.title}`}
    >
      <div
        style={{
          ...styles.profileIcon,
          background: profile.background,
          color: profile.color,
        }}
      >
        {profile.icon}
      </div>

      <div style={styles.profileContent}>
        <h3 style={styles.profileTitle}>{profile.title}</h3>

        <p style={styles.profileDescription}>{profile.description}</p>

        <div
          style={{
            ...styles.profileExamples,
            background: profile.background,
            color: profile.color,
          }}
        >
          {profile.examples}
        </div>
      </div>

      <div style={{ ...styles.profileArrow, color: profile.color }}>→</div>
    </button>
  );
}

function FeatureCard({ icon, title, text }) {
  return (
    <div style={styles.featureCard}>
      <div style={styles.featureIcon}>{icon}</div>
      <h3 style={styles.featureTitle}>{title}</h3>
      <p style={styles.featureText}>{text}</p>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #050816 0%, #0b1020 48%, #f8fafc 48%, #ffffff 100%)",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  topBar: {
  width: "calc(100% - 48px)",
  maxWidth: "1200px",
  margin: "0 auto",
  paddingTop: "28px",
},

backButton: {
  padding: 0,
  border: "none",
  background: "transparent",
  color: "#cbd5e1",
  fontSize: "13px",
  fontWeight: "800",
  cursor: "pointer",
},

  hero: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "48px 24px 90px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "48px",
    alignItems: "center",
  },

  heroContent: {
    color: "#ffffff",
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 14px",
    borderRadius: "999px",
    background: "rgba(124, 58, 237, 0.14)",
    border: "1px solid rgba(167, 139, 250, 0.35)",
    color: "#c4b5fd",
    fontSize: "13px",
    fontWeight: "800",
    marginBottom: "22px",
  },

 title: {
  margin: 0,
  maxWidth: "680px",
  fontSize: "clamp(34px, 4vw, 52px)",
  lineHeight: 1.08,
  letterSpacing: "-0.035em",
  fontWeight: "900",
},

  subtitle: {
    maxWidth: "700px",
    margin: "24px 0 0",
    color: "#cbd5e1",
    fontSize: "17px",
    lineHeight: 1.8,
  },

  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "14px",
    marginTop: "32px",
  },

  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    border: "none",
    borderRadius: "14px",
    padding: "14px 22px",
    background: "linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "800",
    cursor: "pointer",
    boxShadow: "0 14px 32px rgba(37, 99, 235, 0.28)",
  },

  secondaryButton: {
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "14px",
    padding: "14px 22px",
    background: "rgba(255, 255, 255, 0.05)",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "800",
    cursor: "pointer",
  },

  adminButton: {
    padding: "15px 22px",
    border: "1px solid rgba(167, 139, 250, 0.45)",
    borderRadius: "14px",
    background: "rgba(124, 58, 237, 0.14)",
    color: "#ddd6fe",
    fontSize: "14px",
    fontWeight: "800",
    cursor: "pointer",
  },

  accountBar: {
    position: "fixed",
    top: "16px",
    right: "18px",
    zIndex: 9999,
    maxWidth: "calc(100vw - 36px)",
    padding: "9px 10px 9px 12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "15px",
    background: "rgba(7, 11, 30, 0.94)",
    boxShadow: "0 12px 35px rgba(0, 0, 0, 0.28)",
    backdropFilter: "blur(14px)",
    color: "#ffffff",
  },

  accountIdentity: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "9px",
  },

  accountIcon: {
    width: "32px",
    height: "32px",
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "900",
  },

  accountInformation: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  accountLabel: {
    color: "#94a3b8",
    fontSize: "8px",
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },

  accountEmail: {
    maxWidth: "180px",
    overflow: "hidden",
    color: "#ffffff",
    fontSize: "10px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  logoutButton: {
    minHeight: "32px",
    padding: "0 11px",
    flexShrink: 0,
    border: "1px solid rgba(248, 113, 113, 0.32)",
    borderRadius: "9px",
    background: "rgba(239, 68, 68, 0.12)",
    color: "#fca5a5",
    fontSize: "9px",
    fontWeight: "900",
    cursor: "pointer",
  },

  disabledButton: {
    opacity: 0.6,
    cursor: "not-allowed",
  },

  arrow: {
    fontSize: "18px",
  },

  summaryCard: {
    borderRadius: "28px",
    padding: "26px",
    background: "rgba(255, 255, 255, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    boxShadow: "0 28px 80px rgba(0, 0, 0, 0.35)",
    backdropFilter: "blur(20px)",
    color: "#ffffff",
  },

  summaryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
  },

  summaryLabel: {
    color: "#94a3b8",
    fontSize: "12px",
    fontWeight: "700",
  },

  summaryTitle: {
    marginTop: "5px",
    fontSize: "24px",
    fontWeight: "900",
  },

  summaryIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "16px",
    background: "rgba(124, 58, 237, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
  },

  summaryBody: {
    marginTop: "24px",
    padding: "18px",
    borderRadius: "18px",
    background: "rgba(2, 6, 23, 0.55)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
  },

  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    padding: "12px 0",
    borderBottom: "1px solid rgba(255, 255, 255, 0.07)",
  },

  summaryRowLabel: {
    color: "#94a3b8",
    fontSize: "13px",
  },

  summaryRowValue: {
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "800",
  },

  statusBadge: {
    display: "inline-flex",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "rgba(245, 158, 11, 0.14)",
    color: "#fbbf24",
    fontSize: "11px",
    fontWeight: "800",
  },

  progressBlock: {
    marginTop: "22px",
  },

  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    color: "#cbd5e1",
    fontSize: "12px",
  },

  progressTrack: {
    height: "8px",
    marginTop: "10px",
    borderRadius: "999px",
    background: "rgba(255, 255, 255, 0.1)",
    overflow: "hidden",
  },

  progressBar: {
    width: "75%",
    height: "100%",
    borderRadius: "999px",
    background: "linear-gradient(90deg, #7c3aed 0%, #2563eb 100%)",
  },

  profilesSection: {
    background: "#f8fafc",
    padding: "90px 24px",
    scrollMarginTop: "30px",
  },

  profilesGrid: {
    width: "100%",
    maxWidth: "1200px",
    margin: "48px auto 0",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
  },

  profileCard: {
    position: "relative",
    minHeight: "310px",
    padding: "26px",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    background: "#ffffff",
    boxShadow: "0 14px 36px rgba(15, 23, 42, 0.06)",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  profileIcon: {
    width: "58px",
    height: "58px",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "27px",
  },

  profileContent: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
  },

  profileTitle: {
    margin: "22px 0 0",
    color: "#0f172a",
    fontSize: "21px",
    fontWeight: "900",
  },

  profileDescription: {
    margin: "12px 0 0",
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.7,
  },

  profileExamples: {
    display: "inline-flex",
    alignSelf: "flex-start",
    marginTop: "auto",
    padding: "8px 10px",
    borderRadius: "10px",
    fontSize: "11px",
    lineHeight: 1.5,
    fontWeight: "800",
  },

  profileArrow: {
    position: "absolute",
    top: "28px",
    right: "26px",
    fontSize: "22px",
    fontWeight: "900",
  },

  profileNotice: {
    maxWidth: "900px",
    margin: "32px auto 0",
    padding: "16px 20px",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
  },

  noticeIcon: {
    fontSize: "18px",
  },

  noticeText: {
    margin: 0,
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
  },

  featuresSection: {
    background: "#ffffff",
    padding: "90px 24px",
  },

  sectionHeading: {
    maxWidth: "760px",
    margin: "0 auto",
    textAlign: "center",
  },

  sectionEyebrow: {
    color: "#7c3aed",
    fontSize: "12px",
    fontWeight: "900",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
  },

  sectionTitle: {
    margin: "14px 0 0",
    color: "#0f172a",
    fontSize: "clamp(30px, 4vw, 44px)",
    fontWeight: "900",
    letterSpacing: "-0.03em",
  },

  sectionText: {
    margin: "18px auto 0",
    maxWidth: "680px",
    color: "#64748b",
    fontSize: "15px",
    lineHeight: 1.8,
  },

  featuresGrid: {
    maxWidth: "1200px",
    margin: "48px auto 0",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "18px",
  },

  featureCard: {
    padding: "24px",
    borderRadius: "22px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
  },

  featureIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "16px",
    background: "#ede9fe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "23px",
  },

  featureTitle: {
    margin: "20px 0 0",
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: "900",
  },

  featureText: {
    margin: "10px 0 0",
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.7,
  },

  infoSection: {
    background: "#ffffff",
    padding: "0 24px 90px",
  },

  infoCard: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "34px",
    borderRadius: "26px",
    background: "#f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "30px",
    flexWrap: "wrap",
  },

  infoTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "28px",
    fontWeight: "900",
  },

  infoText: {
    margin: "12px 0 0",
    maxWidth: "760px",
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.8,
  },

  darkButton: {
    border: "none",
    borderRadius: "14px",
    padding: "14px 22px",
    background: "#0f172a",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "800",
    cursor: "pointer",
  },
};

export default FinanceHome;